import { useEffect, useId, useRef, useState } from "react";
import { Check, Languages } from "lucide-react";
import { useI18n } from "./LocaleProvider";

/**
 * 语言切换器。
 *
 * - `inline`：登录页等独立区域的并排选项。
 * - `dropdown`：顶栏图标触发 + 纵向菜单（勾选当前语言）。
 */
export function LanguageSwitcher({
  variant = "inline",
  className,
}: {
  variant?: "inline" | "dropdown";
  className?: string;
}) {
  const { locale, locales, setLocale, t } = useI18n();

  if (variant === "dropdown") {
    return <LanguageDropdown className={className} />;
  }

  const classes = ["language-switcher", className ?? ""].filter(Boolean).join(" ");
  return (
    <div className={classes} role="group" aria-label={t("language.switchAria")}>
      <Languages size={15} aria-hidden="true" />
      {locales.map((item) => (
        <button
          key={item}
          type="button"
          className={`language-switcher-option${item === locale ? " active" : ""}`}
          aria-pressed={item === locale}
          onClick={() => setLocale(item)}
        >
          {t(`language.${item}`)}
        </button>
      ))}
    </div>
  );
}

function LanguageDropdown({ className }: { className?: string }) {
  const { locale, locales, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeWithEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOutside);
      window.removeEventListener("keydown", closeWithEscape);
    };
  }, [open]);

  const classes = ["language-dropdown", className ?? ""].filter(Boolean).join(" ");

  return (
    <div className={classes} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="language-dropdown-trigger"
        aria-label={t("language.switchAria")}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <Languages size={18} aria-hidden="true" />
      </button>
      {open ? (
        <div className="language-dropdown-menu" id={menuId} role="menu" aria-label={t("language.label")}>
          {locales.map((item) => {
            const selected = item === locale;
            return (
              <button
                key={item}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                className={`language-dropdown-option${selected ? " selected" : ""}`}
                onClick={() => {
                  setLocale(item);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
              >
                <span>{t(`language.${item}`)}</span>
                {selected ? <Check size={16} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
